import * as is from 'utilities/is';
import Markup from 'preact-markup';
import { h, Component } from 'preact';
import { pick } from 'utilities/data';
import { unique } from 'utilities/list';
import { collectStyles, keyHandler } from 'utilities/dom';
import { getStateLogger } from 'utilities/state';
import { dummyEventBus } from 'utilities/communication';
import { submitForm } from 'utilities/network';
import { LANGUAGE } from 'constants/context';
import { SCORE_OPERATIONS } from 'constants';
import { animatable, clippable } from 'rendering/behaviors';
import { generateInlineLinkAnnotation } from 'utilities/annotations';
import {
    COMPOSITIONS,
    COMPOSITION_TYPES,
    SERIALIZABLE_PROPS,
    LIFECYCLES,
    ACTIONS,
    AT_TIME_MARGIN_SECONDS
} from 'constants/annotations';
import {
    ANNOTATION_EVENT,
    PLAYER_CONTROL_EVENT,
    APPLICATION_EVENT,
    PLAYER_EVENT
} from 'constants/events';
import { VIEW_PAGE } from '../../constants/events';



@clippable
@animatable
export class BaseAnnotation extends Component {
    static scalable = true;

    constructor(props) {
        super(props);
        this.state = {
            ...this.defaultState,
            ...props.state,
            ...props.initialState,
            playing: false
        };
        getStateLogger().addAnnotation(this.props);
        this.eventBus.on(PLAYER_EVENT.PLAY, this.onPlay);
        this.eventBus.on(PLAYER_EVENT.PAUSE, this.onPause);
    }

    setState(updater, callback = () => {}) {
        super.setState(updater, () => {
            getStateLogger().logAnnotationState(this.props.id, this.state);
            this.eventBus.emit(ANNOTATION_EVENT.STATE_CHANGE, this.props.id, this.state);
            callback();
        });
    }

    onPlay = () => {
        this.setState({
            playing: true
        })
    }

    onPause = () => {
        this.setState({
            playing: false
        })
    }

    get isPlaying() {
        return this.state.playing;
    }

    componentDidMount() {
        if (this.options.pause === -1) {
            this.eventBus.emit(PLAYER_CONTROL_EVENT.PAUSE, true);
        }
        this.onAction('onActivate');
        this.checkTimeActions();
    }

    componentDidUpdate(prevProps) {
        this.checkTimeActions(prevProps.currentTime, prevProps);
    }

    onClick = e => {
        // Legacy options handling
    }

    onAction = (eventName, event = {}) => {
        const actionHandlers = this.actions.filter(a => a.event === eventName);
        const customEventData = this.customEventData();
        const args = {};
        const { id } = this.props;

        if (eventName === 'onSubmit' && event.preventDefault) {
            event.preventDefault(); // prevent browser from redirecting
            args.formData = this.formData;
        }

        actionHandlers.forEach(({ action, args = {} }) => {
            if (action === ACTIONS.GO_TO_LINK) {
                if (is.knownTarget(args.target)) {
                    this.eventBus.emit(ANNOTATION_EVENT.ADD, generateInlineLinkAnnotation({
                        id,
                        url: args.value,
                        actions: this.actions,
                        ...args
                    }));
                } else {
                    window.open(args.value, '_blank');
                }
            }

            if (action === ACTIONS.ADD_CLASS) {
                this.eventBus.emit(ANNOTATION_EVENT.ADD_CLASS, {...args});
            }

            if (action === ACTIONS.REMOVE_CLASS){
                this.eventBus.emit(ANNOTATION_EVENT.REMOVE_CLASS, {...args});
            }

            if (action === ACTIONS.SET_CLASS) {
                this.eventBus.emit(ANNOTATION_EVENT.SET_CLASS, {...args});
            }

            if (action === ACTIONS.GO_TO_TIME) {
                this.toTime(args.value);
            }

            if (action === ACTIONS.GO_TO_NEXT) {
                this.eventBus.emit(ANNOTATION_EVENT.NEXT_ITEM, args.value);
            }

            if (action === ACTIONS.GO_TO_PREV) {
                this.eventBus.emit(ANNOTATION_EVENT.PREV_ITEM, args.value);
            }

            if (action === ACTIONS.GO_TO_ANNOTATION) {
                const { startTimes = {} } = this.props;

                if (args.value.includes('#')) {
                    const path = args.value.split('#');

                    this.eventBus.emit(ANNOTATION_EVENT.NAVIGATE_SUB_ELEMENT, {
                        id: path.shift(),
                        path
                    });
                } else if (is.defined(startTimes[args.value])) {
                    this.toTime(startTimes[args.value]);
                }
            }

            if (action === ACTIONS.PAUSE) {
                requestAnimationFrame(this.forcePause);
            }

            if (action === ACTIONS.PLAY) {
                this.eventBus.emit(PLAYER_CONTROL_EVENT.PLAY);
            }

            if (action === ACTIONS.APPLY_GATE) {
                this.applyGate(args.value);
            }

            if (action === ACTIONS.RELEASE_GATE) {
                this.releaseGate(args.value);
            }

            if (action === ACTIONS.SET_VARIABLES) {
                this.eventBus.emit(APPLICATION_EVENT.SET_VARIABLES, args.value);
            }

            if (action === ACTIONS.DISABLE_SEEK) {
                this.eventBus.emit(PLAYER_CONTROL_EVENT.DISABLE_SEEK);
            }

            if (action === ACTIONS.ENABLE_SEEK) {
                this.eventBus.emit(PLAYER_CONTROL_EVENT.ENABLE_SEEK, args);
            }

            if (action === ACTIONS.SET_LANGUAGE) {
                this.eventBus.emit(APPLICATION_EVENT.SET_STATE, LANGUAGE, args.value);
            }

            if (action === ACTIONS.SEND_DATA) {
                const body = new FormData(event.target);

                submitForm(args.value, body, args.method)
                    .then(result => this.eventBus.emit(ANNOTATION_EVENT.DATA_SENT, args.value));
            }

            if (action === ACTIONS.CONFIRM_ACTION) {
                const formatted = { prompt: args.value, ...args };

                if (this.handlesConfirm) {
                    this.showConfirm(formatted);
                } else {
                    this.eventBus.emit(ANNOTATION_EVENT.SHOW_CONFIRM, formatted);
                }
            }

            if (action === ACTIONS.REMOVE_ANNOTATION) {
                this.eventBus.emit(ANNOTATION_EVENT.REMOVE, args.value);
            }

            if (action === ACTIONS.HIDE_ANNOTATION) {
                this.eventBus.emit(ANNOTATION_EVENT.HIDE, args.value || id);
            }

            if (action === ACTIONS.SHOW_ANNOTATION) {
                this.eventBus.emit(ANNOTATION_EVENT.SHOW, args.value || id);
            }

            if (action === ACTIONS.TOGGLE_ANNOTATION) {
                this.eventBus.emit(ANNOTATION_EVENT.TOGGLE, args.value || id);
            }

            if (action === ACTIONS.APPLY_STYLE) {
                this.eventBus.emit(ANNOTATION_EVENT.MODIFY, { id: args.id || id, styles: args.value });
            }

            if (action === ACTIONS.SET_PANEL) {
                this.eventBus.emit(ANNOTATION_EVENT.MODIFY, {
                    id: args.value || id,
                    target: args.target
                });
            }

            if (action === ACTIONS.SUBMIT_FORM) {
                this.eventBus.emit(ANNOTATION_EVENT.SUBMIT_FORM, args.value);
            }

            if (action === ACTIONS.RESET_FORM) {
                this.eventBus.emit(ANNOTATION_EVENT.RESET_FORM, args.value);
            }

            if (action === ACTIONS.UPDATE_SCORE) {
                let eventName;

                if (args.operation === SCORE_OPERATIONS.ADD) {
                    eventName = APPLICATION_EVENT.INCREMENT_SCORE;
                } else if (args.operation === SCORE_OPERATIONS.SUBTRACT) {
                    eventName = APPLICATION_EVENT.DECREMENT_SCORE;
                } else if (args.operation === SCORE_OPERATIONS.ASSIGN) {
                    eventName = APPLICATION_EVENT.SET_SCORE;
                }

                if (eventName) {
                    this.eventBus.emit(eventName, args.value);
                }
            }

            if (action === ACTIONS.RETRY_QUIZ) {
                this.eventBus.emit(ANNOTATION_EVENT.RETRY_QUIZ, args.value);
            }

            if (action === ACTIONS.COMPLETE_QUIZ) {
                this.eventBus.emit(ANNOTATION_EVENT.COMPLETE_QUIZ, args.value);
            }

            if (action === ACTIONS.SET_LEVEL) {
                this.eventBus.emit(APPLICATION_EVENT.LEVEL_CHANGED, args.value);
            }

            if (action === ACTIONS.ENTER_FULLSCREEN) {
                this.eventBus.emit(ANNOTATION_EVENT.ENTER_FULLSCREEN)
            }

            if (action === ACTIONS.EXIT_FULLSCREEN) {
                this.eventBus.emit(ANNOTATION_EVENT.EXIT_FULLSCREEN)
            }

            if (action === ACTIONS.RESET_ACTIVITIES) {
                this.eventBus.emit(VIEW_PAGE.NAV_BACK_TO_QUIZ_LANDING_PAGE)
            }
        });

        // only send event if handled
        if (actionHandlers.length) {
            this.eventBus.emit(ANNOTATION_EVENT.ANNOTATION_ACTION, eventName.replace('on', ''), { ...this.props, ...args, customEventData }, actionHandlers);
        }

        // If class defines a separate handler for this event, call it as well
        if (is.fn(this[eventName])) {
            this[eventName](event);
        }

        if (eventName === 'onActivate') {
            const { id } = this.props;
            this.eventBus.emit(ANNOTATION_EVENT.ACTIVATE, pick(this.props, SERIALIZABLE_PROPS));
            this.eventBus.emit(ANNOTATION_EVENT.LIFECYCLE_CHANGE, { id, status: LIFECYCLES.VISITED })
        }

        if (eventName === 'onDeactivate') {
            this.eventBus.emit(ANNOTATION_EVENT.DEACTIVATE, pick(this.props, SERIALIZABLE_PROPS));
        }
    }
    
    forcePause = () => {
        //This method provides a failsafe for atStartTime events that might not fire due to small time discrepancies. 
        this.eventBus.emit(PLAYER_CONTROL_EVENT.PAUSE, true);
        if (this.isPlaying) {
            requestAnimationFrame(this.forcePause);
        }
    }

    applyActions(actions) {
        const ENTER_KEY = 13,
            SPACEBAR = 32,
            eventTypes = actions.map(a => a.event).filter(unique);

        return eventTypes.reduce((reduced, type) => {
            reduced[type] = this.onAction.bind(this, type);

            // if annotation defines onClick handler, also apply to when enter key pressed
            if (type === 'onClick') {
                reduced['onKeyUp'] = keyHandler([ENTER_KEY, SPACEBAR], this.onAction.bind(this, type));
            }

            return reduced;
        }, {});
    }

    customEventData = () => {
        return {};
    }

    applyGate(id = this.props.id) {
        this.eventBus.emit(ANNOTATION_EVENT.MODIFY, { id, gated: true });
    }

    releaseGate(id = this.props.id) {
        this.eventBus.emit(ANNOTATION_EVENT.MODIFY, { id, gated: false });
        this.eventBus.emit(PLAYER_CONTROL_EVENT.PLAY);
    }

    getElementStyles(options = {}) {
        const styles = collectStyles({
            state: options.state || this.state,
            element: is.compositionType(options) ? options : options.elementName || COMPOSITION_TYPES.ROOT,
            styles: this.styles
        });

        if (this.clickable) {
            styles.cursor = 'pointer';
        }

        return styles;
    }

    componentWillUnmount() {
        if (this.options.gate) {
            this.releaseGate();
        }

        this.onAction('onDeactivate');
    }

    checkTimeActions(lastCurrentTime, prevProps) {
        if (this.props.currentTime === lastCurrentTime || this.props.currentTime - lastCurrentTime < AT_TIME_MARGIN_SECONDS) {
            return;
        }

        if (this.atStartTime) {
            this.onAction('atStartTime');
        }

        if (this.atEndTime) {
            this.onAction('atEndTime');
        }
    }

    toTime = time => {
        this.eventBus.emit(PLAYER_CONTROL_EVENT.CURRENT_TIME, time);
    }

    get classes() {
        const annotationClasses = this.containsMarkup || !this.applyLegacyStyles ? '' : this.legacyClasses;
        const userAddedClasses = this.userClasses;

        return `${annotationClasses} ${userAddedClasses}`;
    }

    get styles() {
        return this.props.styles || {};
    }

    get defaultState() {
        return {}
    }

    get dataAttrs() {
        const attrs = {
            ...this.options.dataAttrs,
            'data-tool-type': this.props.toolType,
            'data-hy-legacy-id': this.props.id,
            'data-localized': this.options.localized ? 'true' : 'false',
            'data-expanded': 'false'
        };

        if (this.props.intent) {
            attrs['data-intent'] = this.props.intent;
        }

        return attrs;
    }

    get contents() {
        const { content = '', contentType } = this.props;

        if (contentType === 'htmlstring') {
            return <Markup markup={ content } type="html" />;
        } else if (contentType === 'template' && Array.isArray(content) && this.options.template) {
            const compiled = content.reduce((template,c,i) => template.replace(`{{${i}}}`,c), this.options.template);
            return <Markup markup={ compiled } type="html" />;
        }

        return content;
    }

    get options() {
        return this.props.properties || {};
    }

    get applyLegacyStyles() {
        return is.defined(this.props.legacy) ? this.props.legacy : false;
    }

    get legacyClasses() {
        return '';
    }

    get userClasses () {
        return this.props.classes ? this.props.classes : '';
    }
    get shouldApplyPosition() {
        return !this.isInAdjacentContainer;
    }

    get isInAdjacentContainer() {
        return this.props.target.includes('adjacent');
    }

    get actions() {
        return this.props.actions || [];
    }

    get clickable() {
        return this.actions.filter(a => a.event === 'onClick' || a.event === 'onDblClick').length > 0;
    }

    get eventBus() {
        return this.props.bus || dummyEventBus();
    }

    get bookmarks() {
        return this.props.bookmarks || [];
    }

    get atStartTime() {
        const { currentTime, start } = this.props;
        return is.closeTo(currentTime, start, AT_TIME_MARGIN_SECONDS);
    }

    get atEndTime() {
        const { currentTime, end } = this.props;
        return is.closeTo(currentTime, end, AT_TIME_MARGIN_SECONDS);
    }

    get handlesConfirm() {
        return is.fn(this.showConfirm);
    }

    get animation() {
        return this.props.animation || {}
    }

    get data() {
        return this.props.data || {};
    }

    get internal() {
        const { internal = { name: '', refId: '' } } = this.props;
        return internal;
    }

    static get composition() {
        return [COMPOSITIONS.SIMPLE];
    }

    static get schemaType() {
        return '';
    }
}
